// src/components/CreateChatDialog.js
import React, { useEffect, useMemo, useState } from 'react';
import {
  Avatar, Box, Chip, CircularProgress, Divider, IconButton,
  InputBase, List, ListItem, ListItemAvatar, ListItemText,
  Tab, Tabs, Typography,
} from '@mui/material';
import { Close as CloseIcon, Check as CheckIcon } from '@mui/icons-material';
import {
  addDoc, collection, getDocs, serverTimestamp,
} from 'firebase/firestore';
import { db } from '../firebase';

const PURPLE = '#7C3AED';

const ROLE_LABELS = { admin: 'Admin', teacher: 'Prof', parent: 'Parent', student: 'Élève' };
const ROLE_COLORS = { admin: '#E53935', teacher: '#1E88E5', parent: '#43A047', student: '#FB8C00' };

const normalizeRole = (r = '') => {
  const v = r.toLowerCase();
  if (v.includes('admin')) return 'admin';
  if (v.includes('prof') || v.includes('teacher')) return 'teacher';
  if (v.includes('parent')) return 'parent';
  return 'student';
};

const RoleChip = ({ role }) => {
  const key = normalizeRole(role);
  return (
    <Box component="span" sx={{
      fontSize: 10, fontWeight: 700, px: 0.8, py: 0.2, borderRadius: 1,
      bgcolor: (ROLE_COLORS[key] || '#999') + '20',
      color: ROLE_COLORS[key] || '#999', ml: 0.5,
    }}>
      {ROLE_LABELS[key] || role}
    </Box>
  );
};

export default function CreateChatDialog({ open, onClose, myUid }) {
  const [tab, setTab] = useState(0); // 0=Groupe libre, 1=Lié à un cours
  const [users, setUsers] = useState([]);
  const [courses, setCourses] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  const [chatName, setChatName] = useState('');
  const [search, setSearch] = useState('');
  const [selected, setSelected] = useState([]); // uids
  const [selectedCourses, setSelectedCourses] = useState([]); // multi-cours
  const [audience, setAudience] = useState('both'); // students | parents | both

  // Chargement users + cours
  useEffect(() => {
    if (!open) return;
    setLoading(true);
    Promise.all([
      getDocs(collection(db, 'users')),
      getDocs(collection(db, 'dance_courses')),
    ]).then(([uSnap, cSnap]) => {
      setUsers(uSnap.docs.map(d => ({ id: d.id, ...d.data() })));
      setCourses(cSnap.docs.map(d => ({ id: d.id, ...d.data() })));
    }).finally(() => setLoading(false));
  }, [open]);

  const reset = () => {
    setChatName(''); setSearch(''); setSelected([]);
    setSelectedCourses([]); setAudience('both'); setTab(0);
  };

  const handleClose = () => { reset(); onClose(); };

  // Nom d'affichage selon la structure Firestore de l'app mobile :
  // firstName + lastName toujours présents, email optionnel (absent pour mineurs)
  const getDisplayName = (u) => {
    const uid = u.uid || u.id;
    if (u.firstName || u.lastName) {
      const full = `${u.firstName || ''} ${u.lastName || ''}`.trim();
      return u.pseudo ? `${full} (${u.pseudo})` : full;
    }
    return u.displayName || u.email?.split('@')[0] || uid.slice(0, 6);
  };

  // Filtrer : uniquement les utilisateurs avec un compte app = ceux qui ont un pseudo
  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return users.filter(u => {
      const uid = u.uid || u.id;
      if (uid === myUid) return false;
      if (!u.pseudo) return false; // pas de pseudo = pas de compte app
      if (!q) return true;
      const name = getDisplayName(u).toLowerCase();
      return name.includes(q) ||
        (u.email || '').toLowerCase().includes(q) ||
        (u.pseudo || '').toLowerCase().includes(q);
    });
  }, [users, search, myUid]);

  // Grouper par rôle
  const grouped = useMemo(() => {
    const g = { admin: [], teacher: [], parent: [], student: [] };
    filtered.forEach(u => {
      const key = normalizeRole(u.role || '');
      if (g[key]) g[key].push(u); else g.student.push(u);
    });
    return g;
  }, [filtered]);

  const toggleUser = (uid) => {
    setSelected(prev => prev.includes(uid) ? prev.filter(x => x !== uid) : [...prev, uid]);
  };

  // Toggle un cours dans la sélection multiple
  const toggleCourse = (courseId) => {
    setSelectedCourses(prev =>
      prev.includes(courseId) ? prev.filter(id => id !== courseId) : [...prev, courseId]
    );
  };

  // Participants auto pour tous les cours sélectionnés combinés
  const courseParticipants = useMemo(() => {
    if (!selectedCourses.length) return [];
    const uids = new Set();
    selectedCourses.forEach(courseId => {
      const c = courses.find(c => c.id === courseId);
      if (!c) return;
      const studentIds = c.studentIds || c.students || [];
      users.forEach(u => {
        const uid = u.uid || u.id;
        if (!u.pseudo) return; // compte app uniquement
        const role = normalizeRole(u.role || '');
        if (audience === 'students' && studentIds.includes(uid)) uids.add(uid);
        else if (audience === 'parents' && role === 'parent') uids.add(uid);
        else if (audience === 'both' && (studentIds.includes(uid) || role === 'parent')) uids.add(uid);
      });
    });
    return [...uids];
  }, [selectedCourses, audience, courses, users]);

  const canCreate = tab === 0
    ? chatName.trim() && selected.length >= 1
    : selectedCourses.length > 0 && chatName.trim();

  const handleCreate = async () => {
    if (!canCreate) return;
    setSaving(true);
    try {
      const participants = tab === 0
        ? [...new Set([myUid, ...selected])]
        : [...new Set([myUid, ...courseParticipants])];

      const chatData = {
        name: chatName.trim(),
        type: tab === 1 ? 'course' : 'group',
        participants,
        createdBy: myUid,
        createdAt: serverTimestamp(),
        lastMessageAt: serverTimestamp(),
        lastMessageText: '',
        ...(tab === 1 && { courseIds: selectedCourses, courseId: selectedCourses[0] || null, audience }),
      };

      await addDoc(collection(db, 'chats'), chatData);
      handleClose();
    } catch (e) {
      console.error(e);
    } finally {
      setSaving(false);
    }
  };

  if (!open) return null;

  return (
    <Box sx={{ position: 'fixed', inset: 0, bgcolor: 'rgba(0,0,0,0.45)', zIndex: 1300,
      display: 'flex', alignItems: 'flex-end', justifyContent: 'center' }}
      onClick={handleClose}>
      <Box sx={{ bgcolor: '#fff', borderRadius: '20px 20px 0 0', width: '100%', maxWidth: 520,
        maxHeight: '90vh', display: 'flex', flexDirection: 'column' }}
        onClick={e => e.stopPropagation()}>

        {/* Header */}
        <Box sx={{ px: 2, py: 1.5, display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          borderBottom: '1px solid #eee' }}>
          <Typography variant="h6" fontWeight="bold">Nouveau chat</Typography>
          <IconButton onClick={handleClose}><CloseIcon /></IconButton>
        </Box>

        {/* Onglets */}
        <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ px: 2, borderBottom: '1px solid #eee',
          '& .MuiTab-root': { textTransform: 'none', fontWeight: 600 },
          '& .Mui-selected': { color: PURPLE },
          '& .MuiTabs-indicator': { bgcolor: PURPLE } }}>
          <Tab label="Groupe libre" />
          <Tab label="Lié à un cours" />
        </Tabs>

        {/* Corps */}
        <Box sx={{ flex: 1, overflow: 'auto', p: 2 }}>
          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
              <CircularProgress sx={{ color: PURPLE }} />
            </Box>
          ) : (
            <>
              {/* Nom du chat */}
              <InputBase fullWidth placeholder="Nom du chat…" value={chatName}
                onChange={e => setChatName(e.target.value)}
                sx={{ bgcolor: '#f5f5f5', borderRadius: 2, px: 2, py: 1, mb: 2, display: 'block',
                  fontWeight: 600 }} />

              {tab === 1 && (
                <>
                  {/* Sélection du cours */}
                  <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: 'block' }}>
                    Cours associé
                  </Typography>
                  <Typography variant="caption" color="text.secondary" sx={{ mb: 0.5, display: 'block' }}>
                    Cliquer pour sélectionner/désélectionner — plusieurs cours possibles
                  </Typography>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 2 }}>
                    {courses.map(c => {
                      const sel = selectedCourses.includes(c.id);
                      const label = c.name || c.title || c.id;
                      return (
                        <Box key={c.id}
                          onClick={(e) => { e.stopPropagation(); toggleCourse(c.id); }}
                          sx={{
                            px: 1.5, py: 0.6, borderRadius: 99, cursor: 'pointer',
                            fontSize: 13, fontWeight: sel ? 700 : 400,
                            border: `1.5px solid ${PURPLE}`,
                            bgcolor: sel ? PURPLE : 'transparent',
                            color: sel ? '#fff' : PURPLE,
                            userSelect: 'none',
                            transition: 'all 0.15s',
                            '&:hover': { bgcolor: sel ? '#6D28D9' : PURPLE + '15' },
                          }}>
                          {sel ? '✓ ' : ''}{label}
                        </Box>
                      );
                    })}
                  </Box>
                  {selectedCourses.length > 0 && (
                    <Typography variant="caption" sx={{ color: PURPLE, mb: 1, display: 'block' }}>
                      {selectedCourses.length} cours sélectionné{selectedCourses.length > 1 ? 's' : ''}
                    </Typography>
                  )}

                  {/* Audience */}
                  <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: 'block' }}>
                    Audience
                  </Typography>
                  <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
                    {[
                      { value: 'students', label: '🎓 Élèves' },
                      { value: 'parents', label: '👨‍👩‍👧 Parents' },
                      { value: 'both', label: '👥 Les deux' },
                    ].map(a => (
                      <Chip key={a.value} label={a.label} onClick={(e) => { e.stopPropagation(); setAudience(a.value); }}
                        variant={audience === a.value ? 'filled' : 'outlined'}
                        sx={{ bgcolor: audience === a.value ? PURPLE : 'transparent',
                          color: audience === a.value ? '#fff' : 'text.primary',
                          borderColor: PURPLE }} />
                    ))}
                  </Box>

                  {selectedCourses.length > 0 && (
                    <Typography variant="caption" color="text.secondary">
                      {courseParticipants.length} participant{courseParticipants.length > 1 ? 's' : ''} seront ajoutés automatiquement
                    </Typography>
                  )}
                </>
              )}

              {tab === 0 && (
                <>
                  {/* Sélectionnés */}
                  {selected.length > 0 && (
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mb: 1.5 }}>
                      {selected.map(uid => {
                        const u = users.find(x => (x.uid || x.id) === uid);
                        const name = u ? getDisplayName(u) : uid.slice(0, 6);
                        return (
                          <Chip key={uid} label={name} size="small"
                            onDelete={() => toggleUser(uid)}
                            sx={{ bgcolor: PURPLE + '15', color: PURPLE }} />
                        );
                      })}
                    </Box>
                  )}

                  {/* Recherche */}
                  <InputBase fullWidth placeholder="Rechercher un participant…" value={search}
                    onChange={e => setSearch(e.target.value)}
                    sx={{ bgcolor: '#f5f5f5', borderRadius: 2, px: 2, py: 0.8, mb: 1, display: 'block' }} />

                  {/* Liste par rôle */}
                  {['admin', 'teacher', 'parent', 'student'].map(role => {
                    const list = grouped[role];
                    if (!list?.length) return null;
                    return (
                      <Box key={role}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, my: 1 }}>
                          <Divider sx={{ flex: 1 }} />
                          <Chip label={ROLE_LABELS[role]} size="small"
                            sx={{ bgcolor: ROLE_COLORS[role] + '15', color: ROLE_COLORS[role], fontWeight: 700 }} />
                          <Divider sx={{ flex: 1 }} />
                        </Box>
                        <List dense disablePadding>
                          {list.map(u => {
                            const uid = u.uid || u.id;
                            const sel = selected.includes(uid);
                            return (
                              <ListItem key={uid} button onClick={() => toggleUser(uid)}
                                sx={{ borderRadius: 2, mb: 0.3,
                                  bgcolor: sel ? PURPLE + '10' : 'transparent',
                                  '&:hover': { bgcolor: PURPLE + '08' } }}>
                                <ListItemAvatar>
                                  <Avatar sx={{ width: 36, height: 36, bgcolor: ROLE_COLORS[role] + '25',
                                    color: ROLE_COLORS[role], fontSize: 14 }}>
                                    {getDisplayName(u)[0]?.toUpperCase() || '?'}
                                  </Avatar>
                                </ListItemAvatar>
                                <ListItemText
                                  primary={getDisplayName(u)}
                                  secondary={u.email || (u.role === 'Élève' ? 'Élève mineur (sans email)' : u.role || '')}
                                  primaryTypographyProps={{ variant: 'body2', fontWeight: sel ? 700 : 400 }}
                                  secondaryTypographyProps={{ variant: 'caption' }}
                                />
                                {sel && <CheckIcon sx={{ color: PURPLE, fontSize: 18 }} />}
                              </ListItem>
                            );
                          })}
                        </List>
                      </Box>
                    );
                  })}
                </>
              )}
            </>
          )}
        </Box>

        {/* Footer */}
        <Box sx={{ p: 2, borderTop: '1px solid #eee' }}>
          <Box onClick={handleCreate}
            sx={{ bgcolor: canCreate ? PURPLE : '#ccc', color: '#fff', borderRadius: 2,
              py: 1.5, textAlign: 'center', cursor: canCreate ? 'pointer' : 'default',
              fontWeight: 700, fontSize: 14, display: 'flex', alignItems: 'center',
              justifyContent: 'center', gap: 1 }}>
            {saving ? <CircularProgress size={20} sx={{ color: '#fff' }} /> : 'Créer le chat'}
          </Box>
        </Box>
      </Box>
    </Box>
  );
}